import { all, call, put, select } from 'redux-saga/effects';
import {
  apiInstrumentsPatch,
  apiInstrumentsDetails,
  apiInstrumentsListGet,
  apiInstrumentsGet,
  apiInstrumentsPerformanceGet,
  apiEtfsListGet,
  apiEtfsBroadAssetClassesGet,
  apiEtfsBenchmarkIndicesGet,
  apiFundsListGet,
  apiFundsBroadAssetClassesGet,
  apiFundsBenchmarkIndicesGet,
  apiInstrumentsKpisGet,
} from '../api/instruments';
import { apiMandatesDetails } from '../api/mandate';
import {
  INSTRUMENTS_PATCH,
  INSTRUMENTS_DETAILS_GET,
  INSTRUMENTS_LIST_GET,
  INSTRUMENTS_GET,
  INSTRUMENTS_PERFORMANCE_GET,
  ETFS_LIST_GET,
  ETFS_BROAD_ASSET_CLASSES_GET,
  ETFS_BENCHMARK_INDICES_GET,
  FUNDS_BENCHMARK_INDICES_GET,
  FUNDS_BROAD_ASSET_CLASSES_GET,
  FUNDS_LIST_GET,
  INSTRUMENTS_KPIS_GET,
} from '../actions/types';
import { createQadraSaga } from './shared';
import { peerCategoriesInstrumentsGet } from './peerCategories';
import { preprocessingFundProfilesFundsGet } from './preprocessingFundProfiles';
import { actionModalOpen } from '../actions';

function* instrumentsPatch({ payload: { from, peerCategoryId, instrumentIds, instrument } }) {
  const calls = instrumentIds.map((id) => call(apiInstrumentsPatch, id, instrument));
  yield all(calls);

  if (from === 'PEER_CATEGORIES_RECOMMEND') {
    yield peerCategoriesInstrumentsGet({ payload: { peerCategoryId } });
    yield put(
      actionModalOpen('ModalRecommendInstrument', {
        instrumentIds,
        recommended: instrument.recommended,
      })
    );
  }

  if (from === 'CLUSTERING_TOOL_RECOMMEND') {
    yield preprocessingFundProfilesFundsGet({ payload: { refresh: true } });
    yield put(
      actionModalOpen('ModalRecommendInstrument', {
        instrumentIds,
        recommended: instrument.recommended,
      })
    );
  }
}

const watchInstrumentsPatch = createQadraSaga(instrumentsPatch, {
  action: INSTRUMENTS_PATCH._REQUEST,
});

function* instrumentsGet({ payload: { instrumentId } }) {
  const { instrument } = yield call(apiInstrumentsGet, instrumentId);

  yield put({
    type: INSTRUMENTS_GET._SUCCESS,
    payload: { instrument },
  });
}

const watchInstrumentsGet = createQadraSaga(instrumentsGet, {
  action: INSTRUMENTS_GET._REQUEST,
});

function* instrumentsListGet({ payload: { onlyEnabled, type, types, orderBy, limit } }) {
  const { instruments } = yield call(apiInstrumentsListGet, {
    onlyEnabled,
    type,
    types,
    orderBy,
    limit,
  });

  yield put({
    type: INSTRUMENTS_LIST_GET._SUCCESS,
    payload: { instruments, orderBy },
  });
}

const watchInstrumentsListGet = createQadraSaga(instrumentsListGet, {
  action: INSTRUMENTS_LIST_GET._REQUEST,
});

export function* instrumentsDetailsGet({ payload: { instruments } }) {
  const storeInstrumentsMap = yield select((s) => s.instruments.items);

  const storeInstruments = [];
  if (Object.keys(storeInstrumentsMap).length > 0) {
    instruments.forEach((instrument) => {
      const storeInstrument = storeInstrumentsMap[instrument.instrumentId];
      if (storeInstrument) storeInstruments.push(storeInstrument);
    });
  }

  if (storeInstruments.length !== instruments.length) {
    const mandateIds = instruments
      .filter((i) => i.productType?.toLowerCase() === 'mandate')
      .map((mandate) => mandate.instrumentId);
    const instrumentIds = instruments
      .filter((i) => i.productType?.toLowerCase() !== 'mandate')
      .map((instrument) => instrument.instrumentId);

    let newInstruments = [];

    if (mandateIds.length > 0) {
      const { mandates } = yield call(apiMandatesDetails, mandateIds);
      newInstruments = mandates;
    }
    if (instrumentIds.length > 0) {
      // eslint-disable-next-line no-shadow
      const { instruments } = yield call(apiInstrumentsDetails, instrumentIds);
      newInstruments = [...newInstruments, ...instruments];
    }

    yield put({
      type: INSTRUMENTS_DETAILS_GET._SUCCESS,
      payload: { instruments: newInstruments },
    });
  }
}

const watchInstrumentsDetailsGet = createQadraSaga(instrumentsDetailsGet, {
  action: INSTRUMENTS_DETAILS_GET._REQUEST,
});

function* instrumentsPerformanceGet({
  payload: { instrumentId, benchmarkId, yearsTag, startDate, compareIds },
}) {
  const { chart } = yield call(apiInstrumentsPerformanceGet, instrumentId, {
    benchmarkId,
    yearsTag,
    startDate,
    compareIds,
  });

  yield put({
    type: INSTRUMENTS_PERFORMANCE_GET._SUCCESS,
    payload: {
      instrumentId,
      performanceChartBenchmarkId: benchmarkId,
      performanceChart: chart,
      compareIds,
      performanceChartYearsTag: yearsTag,
    },
  });
}

const watchInstrumentsPerformanceGet = createQadraSaga(instrumentsPerformanceGet, {
  action: INSTRUMENTS_PERFORMANCE_GET._REQUEST,
});

function* instrumentsKpisGet({ payload: { instrumentId, yearsTag, startDate, endDate } }) {
  const { kpis } = yield call(apiInstrumentsKpisGet, instrumentId, {
    yearsTag,
    startDate,
    endDate,
  });

  yield put({
    type: INSTRUMENTS_KPIS_GET._SUCCESS,
    payload: {
      instrumentId,
      kpis,
    },
  });
}

const watchInstrumentsKpisGet = createQadraSaga(instrumentsKpisGet, {
  action: INSTRUMENTS_KPIS_GET._REQUEST,
});

/* EFTs */

function* etfsListGet({ payload = {} }) {
  const { instruments } = yield call(apiEtfsListGet, payload);

  yield put({
    type: ETFS_LIST_GET._SUCCESS,
    payload: { etfs: instruments, filters: payload },
  });
}

const watchEtfsListGet = createQadraSaga(etfsListGet, {
  action: ETFS_LIST_GET._REQUEST,
});

function* etfsBroadAssetClassesGet() {
  const { broadAssetClasses } = yield call(apiEtfsBroadAssetClassesGet);

  yield put({
    type: ETFS_BROAD_ASSET_CLASSES_GET._SUCCESS,
    payload: { broadAssetClasses },
  });
}

const watchEtfsBroadAssetClassesGet = createQadraSaga(etfsBroadAssetClassesGet, {
  action: ETFS_BROAD_ASSET_CLASSES_GET._REQUEST,
});

function* etfsBenchmarkIndicesGet() {
  const { benchmarkIndices } = yield call(apiEtfsBenchmarkIndicesGet);

  yield put({
    type: ETFS_BENCHMARK_INDICES_GET._SUCCESS,
    payload: { benchmarkIndices },
  });
}

const watchEtfsBenchmarkIndicesGet = createQadraSaga(etfsBenchmarkIndicesGet, {
  action: ETFS_BENCHMARK_INDICES_GET._REQUEST,
});

/* Funds */

function* fundsListGet({ payload = {} }) {
  const { instruments } = yield call(apiFundsListGet, payload);

  yield put({
    type: FUNDS_LIST_GET._SUCCESS,
    payload: { funds: instruments, filters: payload },
  });
}

const watchFundsListGet = createQadraSaga(fundsListGet, {
  action: FUNDS_LIST_GET._REQUEST,
});

function* fundsBroadAssetClassesGet() {
  const { broadAssetClasses } = yield call(apiFundsBroadAssetClassesGet);

  yield put({
    type: FUNDS_BROAD_ASSET_CLASSES_GET._SUCCESS,
    payload: { broadAssetClasses },
  });
}

const watchFundsBroadAssetClassesGet = createQadraSaga(fundsBroadAssetClassesGet, {
  action: FUNDS_BROAD_ASSET_CLASSES_GET._REQUEST,
});

function* fundsBenchmarkIndicesGet() {
  const { benchmarkIndices } = yield call(apiFundsBenchmarkIndicesGet);

  yield put({
    type: FUNDS_BENCHMARK_INDICES_GET._SUCCESS,
    payload: { benchmarkIndices },
  });
}

const watchFundsBenchmarkIndicesGet = createQadraSaga(fundsBenchmarkIndicesGet, {
  action: FUNDS_BENCHMARK_INDICES_GET._REQUEST,
});

export default [
  watchInstrumentsPatch,
  watchInstrumentsGet,
  watchInstrumentsListGet,
  watchInstrumentsDetailsGet,
  watchInstrumentsPerformanceGet,
  watchInstrumentsKpisGet,
  watchEtfsListGet,
  watchEtfsBroadAssetClassesGet,
  watchEtfsBenchmarkIndicesGet,
  watchFundsListGet,
  watchFundsBroadAssetClassesGet,
  watchFundsBenchmarkIndicesGet,
];
